import React, { useContext, useEffect, useState, useMemo } from "react";
import "./UseFreeEstimationModal.css";
import AppContext from "../../../../../context/AppContext";
import { useAuth } from "../../../../../context/AuthContext";
import { fetchUserEstimationsApi, setPremiumEstimationApi } from "../../../../../api/EstimationApi";
import ResourceTable from "../../../../organisms/resourceTable/ResourceTable";
import Loader from "../../../../organisms/loader/Loader";
import FilledButton from "../../../../molecules/buttons/filledButton/FilledButton";
import Searchbar from "../../../../molecules/searchbar/Searchbar";
import { useNavigate } from "react-router-dom";

function UseFreeEstimationModal() {
  const { createNotification, setModalVisible } = useContext(AppContext);
  const { getUuid } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userEstimations, setUserEstimations] = useState([]);
  const [isEstimationsLoading, setIsEstimationsLoading] = useState(true);
  const [selectedEstimation, setSelectedEstimation] = useState(null);
  const [selectedEstimationsUuids, setSelectedEstimationsUuids] = useState([]);

  const estimationTableColumns = [
    { id: 1, key: "name", label: "Nom", visible: true, showBracket: true },
    { id: 2, key: "date", label: "Date", visible: true, showBracket: true },
    { id: 3, key: "type", label: "Type", visible: true, showBracket: true },
  ];

  useEffect(() => {
    fetchEstimations();
  }, []);

  const filteredEstimations = useMemo(() => {
    if (!searchTerm.trim()) return userEstimations;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return userEstimations.filter(estimation => {
      if (estimation.name?.toLowerCase().includes(lowerSearchTerm)) {
        return true;
      }

      const typeDescription =
        estimation.type?.id === 1
          ? "fonds de commerce"
          : estimation.type?.id === 2
            ? "titres de société"
            : "murs commerciaux";

      if (typeDescription.includes(lowerSearchTerm)) {
        return true;
      }

      return false;
    });
  }, [userEstimations, searchTerm]);

  function handleSearch(value) {
    setSearchTerm(value);
  }

  function toggleResourceSelection(e, resource) {
    e.preventDefault();

    const newSelectedUuids = selectedEstimationsUuids.includes(resource.uuid) ? [] : [resource.uuid];

    setSelectedEstimationsUuids(newSelectedUuids);
    setSelectedEstimation(newSelectedUuids.length > 0 ? resource : null);
  }

  function orderEstimations(resources, orderBy) {
    if (!orderBy.key || !orderBy.order) {
      return resources;
    }

    return [...resources].sort((a, b) => {
      let valueA, valueB;

      switch (orderBy.key) {
        case "name":
          valueA = a.name || "";
          valueB = b.name || "";
          break;
        case "date":
          try {
            const dateA = a.creation_date ? new Date(a.creation_date) : new Date(0);
            const dateB = b.creation_date ? new Date(b.creation_date) : new Date(0);

            valueA = !isNaN(dateA.getTime()) ? dateA.getTime() : 0;
            valueB = !isNaN(dateB.getTime()) ? dateB.getTime() : 0;
          } catch (error) {
            console.error("Erreur lors du tri des dates:", error);
            valueA = 0;
            valueB = 0;
          }
          break;
        case "type":
          valueA = a.type?.description || a.type?.id || "";
          valueB = b.type?.description || b.type?.id || "";
          break;
        default:
          return 0;
      }

      let compareResult;
      if (typeof valueA === "string") {
        compareResult = valueA.localeCompare(valueB);
      } else {
        compareResult = valueA - valueB;
      }

      return orderBy.order === "asc" ? compareResult : -compareResult;
    });
  }

  async function fetchEstimations() {
    try {
      setIsEstimationsLoading(true);
      const response = await fetchUserEstimationsApi(getUuid(), false, false);

      const transformedEstimations = [];

      if (response && response.data) {
        Object.keys(response.data).forEach(category => {
          const categoryData = response.data[category];
          if (Array.isArray(categoryData)) {
            categoryData.forEach(estimation => {
              if (!estimation.is_downloadable) {
                const transformedEstimation = {
                  id: estimation.id,
                  uuid: estimation.id.toString(),
                  name:
                    estimation.type.id === 3
                      ? estimation.infos?.nom || "Sans nom"
                      : estimation.infos?.enseigne || estimation.infos?.raison_sociale || "Sans nom",
                  creation_date: estimation.date_creation_estimation,
                  validated: estimation.validated,
                  archived: estimation.archived,
                  type: estimation.type,
                  steps_status: estimation.steps_status,
                  is_downloadable: estimation.is_downloadable,
                  originalEstimation: estimation,
                };

                if (estimation.id && estimation.type) {
                  transformedEstimations.push(transformedEstimation);
                }
              }
            });
          }
        });
      }

      setUserEstimations(transformedEstimations);
      setIsEstimationsLoading(false);
    } catch (error) {
      createNotification(
        <>Une erreur est survenue lors du chargement des estimations. Veuillez réessayer.</>,
        "var(--red)",
        "var(--dark-blue)",
      );
      setIsEstimationsLoading(false);
    }
  }

  function getEstimationColumnContent(key, estimation) {
    switch (key) {
      case "name":
        return estimation.name;
      case "date":
        return estimation.creation_date?.split(" ")[0];
      case "type":
        if (estimation.type?.id === 1) {
          return <div className='crm-tag crm-blue-text'>Fonds de commerce</div>;
        } else if (estimation.type?.id === 2) {
          return <div className='crm-tag crm-gold-text'>Titres de société</div>;
        } else {
          return <div className='crm-tag crm-grey-text'>Murs commerciaux</div>;
        }
      default:
        return "";
    }
  }

  async function handleDownloadEstimation() {
    if (!selectedEstimation) return;

    setIsLoading(true);
    try {
      await setPremiumEstimationApi(selectedEstimation.id);
      navigate(`/synthese/${selectedEstimation.id}`);
    } catch (error) {
      console.log(error);
      createNotification(
        <>Une erreur est survenue lors de la préparation de votre estimation offerte. Veuillez réessayer.</>,
        "var(--red)",
        "var(--dark-blue)",
      );
    } finally {
      setIsLoading(false);
      setModalVisible(false);
    }
  }

  return (
    <>
      <p>
        Vous bénéficiez maintenant d'une estimation offerte.
        <br />
        En cliquant sur le bouton ci-dessous, vous pourrez saisir votre estimation et la modifier pendant un mois
        complet.
      </p>
      <div className='free-estimation-options-container'>
        <h3>Mes estimations existantes</h3>
        {isEstimationsLoading ? (
          <div className='centered'>
            <Loader visible={true} preventScrolling={false} className='mini-loader' />
          </div>
        ) : (
          <>
            <Searchbar
              placeholder="Rechercher par nom ou type d'estimation"
              onChange={handleSearch}
              width='100%'
              className='estimation-searchbar'
              bgColor='var(--white)'
            />
            <ResourceTable
              tableColumns={estimationTableColumns}
              resources={filteredEstimations}
              resourceTypeDescription='estimations'
              getColumnContentFunction={getEstimationColumnContent}
              onResourceClick={toggleResourceSelection}
              checkboxesArray={selectedEstimationsUuids}
              setCheckboxesArrayFunction={toggleResourceSelection}
              noResultText='Aucune estimation disponible'
              className='free-estimation-table'
              useCheckbox={true}
              hideHeaderCheckbox={true}
              openResourceInNewTab={false}
              getOrderedResourcesFunction={orderEstimations}
            />
          </>
        )}
      </div>
      <div className='modal-buttons-row'>
        <FilledButton onClick={() => setModalVisible(false)} to='/debut-parcours' padding='10px 20px'>
          Commencer mon estimation
        </FilledButton>
        <FilledButton
          onClick={handleDownloadEstimation}
          padding='10px 20px'
          disabled={!selectedEstimation}
          isLoading={isLoading}
          disabledtooltip={"Veuillez sélectionner une estimation"}>
          Télécharger l'estimation sélectionnée
        </FilledButton>
      </div>
    </>
  );
}

export default UseFreeEstimationModal;
